import { Component, input, AfterViewInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import type { ComposeOption } from 'echarts/core';
import type { BarSeriesOption } from 'echarts/charts';
import type {
  TooltipComponentOption,
  GridComponentOption,
  TitleComponentOption,
  LegendComponentOption
} from 'echarts/components';

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer
]);

type EChartsOption = ComposeOption<
  BarSeriesOption |
  TooltipComponentOption |
  GridComponentOption |
  TitleComponentOption |
  LegendComponentOption
>;

interface IMonthlyData {
  weekday: { m: string; v: number }[];
  saturday: { m: string; v: number }[];
  sunday: { m: string; v: number }[];
}

@Component({
  selector: 'app-insights-ff-lcr-rolling-year',
  standalone: false,
  templateUrl: './insights-ff-lcr-rolling-year.component.html',
  styleUrl: './insights-ff-lcr-rolling-year.component.css',
})
export class InsightsFfLcrRollingYearComponent {
  @ViewChild('chart') chartElement!: ElementRef<HTMLDivElement>;

  data = input.required<IMonthlyData>();

  private chartInstance!: echarts.ECharts;

  ngAfterViewInit(): void {
    const chartDom = this.chartElement.nativeElement;
    this.chartInstance = echarts.init(chartDom);

    const inputData = this.data();

    // Define chronological order using the format in the data (e.g., "2025 Mar")

    // All datasets match weekday order
    const monthOrder = inputData.weekday.map(d => d.m);

    // Sort each series chronologically
    const sortSeries = (series: { m: string; v: number }[]) =>
      [...series].sort((a, b) => monthOrder.indexOf(a.m) - monthOrder.indexOf(b.m));

    const sortedWeekday = sortSeries(inputData.weekday);
    const sortedSaturday = sortSeries(inputData.saturday);
    const sortedSunday = sortSeries(inputData.sunday);

    // Determine unique months across all series for x-axis
    const allMonths = new Set([
      ...sortedWeekday.map(d => d.m),
      ...sortedSaturday.map(d => d.m),
      ...sortedSunday.map(d => d.m)
    ]);
    const xAxisData = Array.from(allMonths).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));

    // Map short month names (e.g., "Mar 2025")
    const shortMonthMap: Record<string, string> = {
      Jan: 'Jan',
      Feb: 'Feb',
      Mar: 'Mar',
      Apr: 'Apr',
      May: 'May',
      Jun: 'Jun',
      Jul: 'Jul',
      Aug: 'Aug',
      Sep: 'Sep',
      Oct: 'Oct',
      Nov: 'Nov',
      Dec: 'Dec'
    };

    const displayXAxisData = xAxisData.map((item: string) => {
      const [year, monthAbbr] = item.trim().split(' ');
      const normalisedMonth = shortMonthMap[monthAbbr] || monthAbbr;
      return `${normalisedMonth} ${year}`;
    });

    // Helper to get value for a month, default 0 if missing
    const getValue = (series: { m: string; v: number }[], month: string) =>
      series.find(d => d.m === month)?.v ?? 0;

    const weekdayData = xAxisData.map(m => getValue(sortedWeekday, m));
    const saturdayData = xAxisData.map(m => getValue(sortedSaturday, m));
    const sundayData = xAxisData.map(m => getValue(sortedSunday, m));

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          let result = params[0].name + '<br/>';
          params.forEach((param: any) => {
            if (param.value !== 0) {
              result += `${param.seriesName}: ${param.value}<br/>`;
            }
          });
          return result;
        }
      },
      legend: {
        data: ['Weekday', 'Saturday', 'Sunday'],
        top: '5%'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: displayXAxisData,
        axisLabel: {
          rotate: 45,
          fontSize: 12,
          interval: 0
        }
      },
      yAxis: {
        type: 'value',
        name: 'Total'
      },
      series: [
        {
          name: 'Weekday',
          type: 'bar',
          data: weekdayData,
          itemStyle: {
            color: '#5470c6'  // blue
          },
          label: {
            show: true,
            position: 'top',
            formatter: (param: any) => param.value === 0 ? '' : param.value
          }
        },
        {
          name: 'Saturday',
          type: 'bar',
          data: saturdayData,
          itemStyle: {
            color: '#91cc75'  // green
          },
          label: {
            show: true,
            position: 'top',
            formatter: (param: any) => param.value === 0 ? '' : param.value
          }
        },
        {
          name: 'Sunday',
          type: 'bar',
          data: sundayData,
          itemStyle: {
            color: '#91a7c6'  // grey-blue
          },
          label: {
            show: true,
            position: 'top',
            formatter: (param: any) => param.value === 0 ? '' : param.value
          }
        }
      ]
    };

    this.chartInstance.setOption(option);
  }

  ngOnDestroy(): void {
    this.chartInstance?.dispose();
  }
}
